/**
 * Check if an action is disabled for a given row
 * @param {Object} action - The action object
 * @param {Object} row - The row data
 * @returns {boolean}
 */
export const isActionDisabled = (action, row) => {
  if (action.disabled) {
    return action.disabled(row)
  }
  return false
}

/**
 * Filter actions to only those applicable to single rows (have onClickSingle or href)
 * @param {Array} actions - Array of action objects
 * @returns {Array}
 */
export const getSingleRowActions = (actions) => {
  return actions.filter((action) => action.onClickSingle || action.href)
}

/**
 * Filter actions to only those visible for a given row (not hidden)
 * @param {Array} actions - Array of action objects
 * @param {Object} row - The row data
 * @returns {Array}
 */
export const getVisibleActions = (actions, row) => {
  return actions.filter((action) => {
    if (action.hidden) {
      return !action.hidden(row)
    }
    return true
  })
}

/**
 * Sort actions so role:"view" actions always appear first
 * @param {Array} actions - Array of action objects
 * @returns {Array}
 */
export const sortViewFirst = (actions) => {
  return [...actions].sort((a, b) => {
    if (a.role === "view" && b.role !== "view") return -1
    if (a.role !== "view" && b.role === "view") return 1
    return 0
  })
}

/**
 * Sort actions so link actions (href) always appear last.
 * role:"view" actions are exempt — they have href but belong at the front.
 * @param {Array} actions - Array of action objects
 * @returns {Array}
 */
export const sortActionsLinksLast = (actions) => {
  return [...actions].sort((a, b) => {
    const aIsLink = a.href && a.role !== "view"
    const bIsLink = b.href && b.role !== "view"
    if (aIsLink && !bIsLink) return 1
    if (!aIsLink && bIsLink) return -1
    return 0
  })
}

/**
 * Convert actions to ActionMenu item format
 * @param {Array} actions - Array of action objects
 * @param {Object} row - The row data
 * @param {Function} onRequestConfirm - Callback for actions requiring confirmation
 * @returns {Array}
 */
export const toMenuItems = (actions, row, onRequestConfirm) => {
  return actions.map((action) => ({
    label: action.label,
    icon: action.icon,
    iconStyle: action.iconStyle,
    disabled: isActionDisabled(action, row),
    disabledReason: isActionDisabled(action, row)
      ? action.disabledReason?.(row)
      : undefined,
    href: action.href?.(row),
    onClick: () => {
      if (action.confirm && onRequestConfirm) {
        onRequestConfirm(action, row)
      } else {
        action.onClickSingle?.(row)
      }
    },
  }))
}

/**
 * Tailwind classes for disabled anchor tags
 */
export const DISABLED_ANCHOR_CLASSES =
  "pointer-events-none cursor-not-allowed opacity-40"
